import {
  autoUpdate,
  platform,
  type Rect,
  type VirtualElement,
} from '@floating-ui/dom';

import { cascadeCSS } from './cascade.js';
import { getCSSPropertyValue, getOffsetParent } from './dom.js';
import { fetchCSS } from './fetch.js';
import {
  type AnchorFunction,
  type AnchorFunctionDeclaration,
  type AnchorPositions,
  parseCSS,
  type TryBlock,
} from './parse.js';
import {
  type InsetValue,
  POSITION_AREA_CASCADE_PROPERTY,
  POSITION_AREA_TARGET_ATTRIBUTE,
  POSITION_AREA_WRAPPER_ATTRIBUTE,
  type PositionAreaContainingBlock,
  type PositionAreaTargetData,
} from './position-area.js';
import {
  type AnchorSide,
  type AnchorSize,
  type InsetProperty,
  isAcceptedAnchorSizeProp,
  isInsetProp,
  type SizingProperty,
} from './syntax.js';
import { insertGeneratedStyles, transformCSS } from './transform.js';
import {
  type GeneratedStyles,
  reportParseErrorsOnFailure,
  resetParseErrors,
  strategyForElement,
} from './utils.js';

export const resolveLogicalSideKeyword = (side: AnchorSide, rtl: boolean) => {
  let percentage: number | undefined;
  switch (side) {
    case 'start':
    case 'self-start':
      percentage = 0;
      break;
    case 'end':
    case 'self-end':
      percentage = 100;
      break;
    default:
      if (typeof side === 'number' && !Number.isNaN(side)) {
        percentage = side;
      }
  }
  if (percentage !== undefined) {
    return rtl ? 100 - percentage : percentage;
  }
  return undefined;
};

export const resolveLogicalSizeKeyword = (
  size: AnchorSize,
  vertical: boolean,
) => {
  let resolved: 'width' | 'height' | undefined;
  switch (size) {
    case 'block':
    case 'self-block':
      resolved = vertical ? 'width' : 'height';
      break;
    case 'inline':
    case 'self-inline':
      resolved = vertical ? 'height' : 'width';
      break;
  }
  return resolved;
};

// This should also check the writing-mode
// See:
// https://github.com/oddbird/css-anchor-positioning/pull/22#discussion_r966348526
// https://trello.com/c/KnqCnHx3
export const getAxis = (position?: string) => {
  switch (position) {
    case 'top':
    case 'bottom':
    case 'inset-block-start':
    case 'inset-block-end':
      return 'y';
    case 'left':
    case 'right':
    case 'inset-inline-start':
    case 'inset-inline-end':
      return 'x';
  }
  return null;
};

export const getAxisProperty = (axis: 'x' | 'y' | null) => {
  switch (axis) {
    case 'x':
      return 'width';
    case 'y':
      return 'height';
  }
  return null;
};

const isInline = (el: HTMLElement) =>
  getCSSPropertyValue(el, 'display') === 'inline';

const getBorders = (el: HTMLElement, axis: 'x' | 'y') => {
  const props =
    axis === 'x'
      ? ['border-left-width', 'border-right-width']
      : ['border-top-width', 'border-bottom-width'];
  return (
    props.reduce(
      (total, prop) => total + parseFloat(getCSSPropertyValue(el, prop)),
      0,
    ) || 0
  );
};

const getBorder = (el: HTMLElement, dir: 'top' | 'right' | 'bottom' | 'left') =>
  parseFloat(getComputedStyle(el).getPropertyValue(`border-${dir}-width`)) || 0;

// Read the element's used margins from its computed style. Unlike reading the
// shifted `--margin-*` custom properties, this resolves the `margin` shorthand,
// logical margins, percentages, and `auto` to physical pixel values, and
// reflects any margin applied by the currently-active position fallback.
const getMargins = (el: HTMLElement) => {
  const style = getComputedStyle(el);
  const margin = (dir: 'top' | 'right' | 'bottom' | 'left') =>
    parseFloat(style.getPropertyValue(`margin-${dir}`)) || 0;
  return {
    top: margin('top'),
    right: margin('right'),
    bottom: margin('bottom'),
    left: margin('left'),
  };
};

export interface GetPixelValueOpts {
  targetEl?: HTMLElement;
  targetProperty: InsetProperty | SizingProperty | 'position-area';
  anchorRect?: Rect;
  anchorSide?: AnchorSide;
  anchorSize?: AnchorSize;
  fallback?: string | null;
}

export const getPixelValue = async ({
  targetEl,
  targetProperty,
  anchorRect,
  anchorSide,
  anchorSize,
  fallback = null,
}: GetPixelValueOpts) => {
  if (!((anchorSize || anchorSide !== undefined) && targetEl && anchorRect)) {
    return fallback;
  }
  if (anchorSize) {
    if (!isAcceptedAnchorSizeProp(targetProperty)) {
      return fallback;
    }
    // Calculate value for `anchor-size()` fn...
    let size: AnchorSize | undefined;
    switch (anchorSize) {
      case 'width':
      case 'height':
        size = anchorSize;
        break;
      default: {
        // Logical keywords require checking the writing-mode
        // of the target element (or its containing block):
        // `block` and `inline` should use the writing-mode of the element's
        // containing block, not the element itself:
        // https://trello.com/c/KnqCnHx3
        const writingMode = getCSSPropertyValue(targetEl, 'writing-mode');
        const vertical =
          writingMode.startsWith('vertical-') ||
          writingMode.startsWith('sideways-');
        size = resolveLogicalSizeKeyword(anchorSize, vertical);
      }
    }
    if (size) {
      return `${anchorRect[size]}px`;
    }
    return fallback;
  }

  if (anchorSide !== undefined) {
    // Calculate value for `anchor()` fn...
    let percentage: number | undefined;
    let offsetParent;
    const axis = getAxis(targetProperty);

    // anchor() can only be assigned to inset properties,
    // and if a physical keyword ('left', 'right', 'top', 'bottom') is used,
    // the axis of the keyword must match the axis of the inset property:
    // https://drafts.csswg.org/css-anchor-1/#queries
    if (!(
      isInsetProp(targetProperty) &&
      axis &&
      (!isInsetProp(anchorSide) || axis === getAxis(anchorSide))
    )) {
      return fallback;
    }
    const startwardProperties = [
      'top',
      'left',
      'inset-block-start',
      'inset-inline-start',
    ];
    const endwardProperties = [
      'bottom',
      'right',
      'inset-block-end',
      'inset-inline-end',
    ];

    switch (anchorSide) {
      case 'left':
      case 'top':
        percentage = 0;
        break;
      case 'right':
      case 'bottom':
        percentage = 100;
        break;
      case 'center':
        percentage = 50;
        break;
      case 'inside':
        percentage = startwardProperties.includes(targetProperty) ? 0 : 100;
        break;
      case 'outside':
        percentage = startwardProperties.includes(targetProperty) ? 100 : 0;
        break;
      default:
        // Logical keywords require checking the writing direction
        // of the target element (or its containing block)
        if (targetEl) {
          // `start` and `end` should use the writing-mode of the element's
          // containing block, not the element itself:
          // https://trello.com/c/KnqCnHx3
          const rtl = (await platform.isRTL?.(targetEl)) || false;
          percentage = resolveLogicalSideKeyword(anchorSide, rtl);
        }
    }

    const hasPercentage =
      typeof percentage === 'number' && !Number.isNaN(percentage);

    const dir = getAxisProperty(axis);
    if (hasPercentage && dir) {
      if (endwardProperties.includes(targetProperty)) {
        offsetParent = await getOffsetParent(targetEl);
      }
      let value =
        anchorRect[axis] + anchorRect[dir] * ((percentage as number) / 100);
      switch (targetProperty) {
        case 'bottom':
        case 'inset-block-end': {
          if (!offsetParent) {
            break;
          }
          let offsetHeight = offsetParent.clientHeight;
          // This is a hack for inline elements with `clientHeight: 0`,
          // but it doesn't take scrollbar size into account
          if (offsetHeight === 0 && isInline(offsetParent)) {
            const border = getBorders(offsetParent, axis);
            offsetHeight = offsetParent.offsetHeight - border;
          }
          value = offsetHeight - value;
          break;
        }
        case 'right':
        case 'inset-inline-end': {
          if (!offsetParent) {
            break;
          }
          let offsetWidth = offsetParent.clientWidth;
          // This is a hack for inline elements with `clientWidth: 0`,
          // but it doesn't take scrollbar size into account
          if (offsetWidth === 0 && isInline(offsetParent)) {
            const border = getBorders(offsetParent, axis);
            offsetWidth = offsetParent.offsetWidth - border;
          }
          value = offsetWidth - value;
          break;
        }
      }
      return `${value}px`;
    }
  }

  return fallback;
};

// Use `isPositionAreaDeclaration` instead for type narrowing AST nodes.
const isPositionAreaTarget = (
  value: AnchorFunction | PositionAreaTargetData,
): value is PositionAreaTargetData => {
  return 'targetUUID' in value;
};

const isAnchorFunction = (
  value: AnchorFunction | PositionAreaTargetData,
): value is AnchorFunction => {
  return 'uuid' in value;
};

// Resolve the alignment within the position-area grid area to a pair of inset
// values for one axis. `start` and `end` alignment only set the inset on the
// aligned side, so the target keeps its natural size and position based on
// normal absolute positioning rules. `center` alignment computes the centered
// offset from the grid area size and the target's margin-box size.
export const resolveAxisInsetValues = (
  alignment: 'start' | 'end' | 'center',
  areaStart: string | null,
  areaEnd: string | null,
  containingBlockSize: number,
  targetMarginBoxSize: number,
): [string, string] => {
  switch (alignment) {
    case 'start':
      return [areaStart ?? '0px', 'auto'];
    case 'end':
      return ['auto', areaEnd ?? '0px'];
    case 'center': {
      const start = parseFloat(areaStart ?? '0');
      const end = parseFloat(areaEnd ?? '0');
      const offset =
        start + (containingBlockSize - start - end - targetMarginBoxSize) / 2;
      return [`${offset}px`, 'auto'];
    }
  }
};

async function applyAnchorPositions(
  declarations: AnchorFunctionDeclaration,
  useAnimationFrame = false,
) {
  const root = document.documentElement;

  for (const [property, anchorValues] of Object.entries(declarations) as [
    InsetProperty | SizingProperty | 'position-area',
    (AnchorFunction | PositionAreaTargetData)[],
  ][]) {
    for (const anchorValue of anchorValues) {
      const anchor = anchorValue.anchorEl;
      const target = anchorValue.targetEl;
      if (anchor && target) {
        const strategy = strategyForElement(target);
        if (isPositionAreaTarget(anchorValue)) {
          // When the target is wrapped, the target is nested in an element that
          // approximates the containing block created by `position-area`, and
          // the insets and alignments are applied through CSS on the wrapper
          // and target. Otherwise the alignment is resolved to inset values
          // that are applied directly to the target.
          const wrapper = anchorValue.wrapperEl;
          const floating = wrapper ?? target;
          const getPositionAreaPixelValue = async (
            inset: InsetValue,
            targetProperty: GetPixelValueOpts['targetProperty'],
            anchorRect: GetPixelValueOpts['anchorRect'],
          ) => {
            if (inset === 0) return '0px';
            return await getPixelValue({
              targetEl: floating,
              targetProperty: targetProperty,
              anchorRect: anchorRect,
              anchorSide: inset,
            });
          };

          autoUpdate(
            anchor,
            floating,
            async () => {
              // Check which `position-area` declaration would win based on the
              // cascade, and apply an attribute on the wrapper (or the target,
              // when there is no wrapper). This activates the generated CSS
              // styles that map the inset and alignment values to their
              // respective properties.
              const appliedId = getCSSPropertyValue(
                target,
                POSITION_AREA_CASCADE_PROPERTY,
              );
              if (wrapper) {
                wrapper.setAttribute(
                  POSITION_AREA_WRAPPER_ATTRIBUTE,
                  appliedId,
                );
              } else {
                target.setAttribute(POSITION_AREA_TARGET_ATTRIBUTE, appliedId);
              }

              const rects = await platform.getElementRects({
                reference: anchor,
                floating,
                // Use the strategy for the floating element, which for a
                // wrapper should match the target it wraps.
                strategy: strategyForElement(floating),
              });
              const insets = anchorValue.insets;

              // Insets of the position-area grid area, relative to the target's
              // containing block.
              const areaTop = await getPositionAreaPixelValue(
                insets.block[0],
                'top',
                rects.reference,
              );
              const areaBottom = await getPositionAreaPixelValue(
                insets.block[1],
                'bottom',
                rects.reference,
              );
              const areaLeft = await getPositionAreaPixelValue(
                insets.inline[0],
                'left',
                rects.reference,
              );
              const areaRight = await getPositionAreaPixelValue(
                insets.inline[1],
                'right',
                rects.reference,
              );

              let topInset, bottomInset, leftInset, rightInset;
              if (wrapper) {
                // The grid area insets are applied to the wrapper, and the
                // target is aligned within it through `justify-self` and
                // `align-self`.
                [topInset, bottomInset, leftInset, rightInset] = [
                  areaTop,
                  areaBottom,
                  areaLeft,
                  areaRight,
                ];
                root.style.setProperty(
                  `${anchorValue.targetUUID}-justify-self`,
                  anchorValue.alignments.inline,
                );
                root.style.setProperty(
                  `${anchorValue.targetUUID}-align-self`,
                  anchorValue.alignments.block,
                );
              } else {
                // Without a wrapper, resolve the target's alignment within the
                // grid area to physical inset values applied to the target.
                const offsetParent = await getOffsetParent(target);
                const margins = getMargins(target);

                [topInset, bottomInset] = resolveAxisInsetValues(
                  anchorValue.alignments.block,
                  areaTop,
                  areaBottom,
                  offsetParent.clientHeight,
                  rects.floating.height + margins.top + margins.bottom,
                );
                [leftInset, rightInset] = resolveAxisInsetValues(
                  anchorValue.alignments.inline,
                  areaLeft,
                  areaRight,
                  offsetParent.clientWidth,
                  rects.floating.width + margins.left + margins.right,
                );
              }

              root.style.setProperty(
                `${anchorValue.targetUUID}-top`,
                topInset || null,
              );
              root.style.setProperty(
                `${anchorValue.targetUUID}-left`,
                leftInset || null,
              );
              root.style.setProperty(
                `${anchorValue.targetUUID}-right`,
                rightInset || null,
              );
              root.style.setProperty(
                `${anchorValue.targetUUID}-bottom`,
                bottomInset || null,
              );
            },
            { animationFrame: useAnimationFrame },
          );
        } else {
          autoUpdate(
            anchor,
            target,
            async () => {
              const rects = await platform.getElementRects({
                reference: anchor,
                floating: target,
                strategy,
              });
              const resolved = await getPixelValue({
                targetEl: target,
                targetProperty: property,
                anchorRect: rects.reference,
                anchorSide: anchorValue.anchorSide,
                anchorSize: anchorValue.anchorSize,
                fallback: anchorValue.fallbackValue,
              });
              root.style.setProperty(anchorValue.uuid, resolved);
            },
            { animationFrame: useAnimationFrame },
          );
        }
      } else if (isAnchorFunction(anchorValue)) {
        // Use fallback value
        const resolved = await getPixelValue({
          targetProperty: property,
          anchorSide: anchorValue.anchorSide,
          anchorSize: anchorValue.anchorSize,
          fallback: anchorValue.fallbackValue,
        });
        root.style.setProperty(anchorValue.uuid, resolved);
      }
    }
  }
}

// How far the target's margin-box overflows each edge of its containing block.
// Positive values indicate overflow on that side.
//
// Per spec, position-try options are evaluated against the target's
// inset-modified containing block — not the scrollport of an arbitrary ancestor
// scroll container. We therefore measure against the target's `offsetParent`
// (its containing block), which is what native browsers use. Both rects are
// read in viewport coordinates via `getBoundingClientRect`, so the page's
// scroll offset cancels out regardless of how far down the document the target
// is. See https://github.com/oddbird/css-anchor-positioning/issues/279.
function checkOverflow(target: HTMLElement, offsetParent: HTMLElement) {
  const targetRect = target.getBoundingClientRect();
  const margin = getMargins(target);
  const marginBox = {
    top: targetRect.top - margin.top,
    bottom: targetRect.bottom + margin.bottom,
    left: targetRect.left - margin.left,
    right: targetRect.right + margin.right,
  };

  // The containing block for an absolutely positioned element is the padding
  // box of its `offsetParent`. When there is no element offsetParent (e.g. a
  // fixed-positioned target), the containing block is the viewport.
  const containingBlock: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  } =
    offsetParent === document.documentElement
      ? {
          top: 0,
          left: 0,
          right: document.documentElement.clientWidth,
          bottom: document.documentElement.clientHeight,
        }
      : (() => {
          const parentRect = offsetParent.getBoundingClientRect();
          return {
            top: parentRect.top + getBorder(offsetParent, 'top'),
            left: parentRect.left + getBorder(offsetParent, 'left'),
            right: parentRect.right - getBorder(offsetParent, 'right'),
            bottom: parentRect.bottom - getBorder(offsetParent, 'bottom'),
          };
        })();

  return {
    top: containingBlock.top - marginBox.top,
    bottom: marginBox.bottom - containingBlock.bottom,
    left: containingBlock.left - marginBox.left,
    right: marginBox.right - containingBlock.right,
  };
}

async function applyPositionFallbacks(
  targetSel: string,
  fallbacks: TryBlock[],
  useAnimationFrame = false,
) {
  if (!fallbacks.length) {
    return;
  }

  const targets: NodeListOf<HTMLElement> = document.querySelectorAll(targetSel);

  for (const target of targets) {
    let checking = false;
    const offsetParent = await getOffsetParent(target);

    autoUpdate(
      // We're just checking whether the target element overflows, so we don't
      // care about the position of the anchor element in this case. Passing in
      // an empty object instead of a reference element avoids unnecessarily
      // watching for irrelevant changes.
      {} as VirtualElement,
      target,
      async () => {
        // If this auto-update was triggered while the polyfill is already
        // looping through the possible `position-try-fallbacks` blocks, do not
        // check again.
        if (checking) {
          return;
        }
        checking = true;
        target.removeAttribute('data-anchor-polyfill');
        const defaultOverflow = checkOverflow(target, offsetParent);
        // If none of the sides overflow, don't try fallbacks
        if (Object.values(defaultOverflow).every((side) => side <= 0)) {
          target.removeAttribute('data-anchor-polyfill-last-successful');
          checking = false;
          return;
        }
        // Apply the styles from each fallback block (in order), stopping when
        // we reach one that does not cause the target's margin-box to overflow
        // its offsetParent (containing block).
        for (const [index, { uuid }] of fallbacks.entries()) {
          target.setAttribute('data-anchor-polyfill', uuid);

          const overflow = checkOverflow(target, offsetParent);

          // If none of the sides overflow, use this fallback and stop loop.
          if (Object.values(overflow).every((side) => side <= 0)) {
            target.setAttribute('data-anchor-polyfill-last-successful', uuid);
            checking = false;
            break;
          }
          // If it's the last fallback, and none have matched, revert to the
          // last successful fallback.
          if (index === fallbacks.length - 1) {
            const lastSuccessful = target.getAttribute(
              'data-anchor-polyfill-last-successful',
            );
            if (lastSuccessful) {
              target.setAttribute('data-anchor-polyfill', lastSuccessful);
            } else {
              target.removeAttribute('data-anchor-polyfill');
            }
            checking = false;
            break;
          }
        }
      },
      { animationFrame: useAnimationFrame, layoutShift: false },
    );
  }
}

async function position(rules: AnchorPositions, useAnimationFrame = false) {
  for (const pos of Object.values(rules)) {
    // Handle `anchor()` and `anchor-size()` functions and `position-area`
    // properties..
    await applyAnchorPositions(pos.declarations ?? {}, useAnimationFrame);
  }

  for (const [targetSel, position] of Object.entries(rules)) {
    // Handle `@position-try` blocks...
    await applyPositionFallbacks(
      targetSel,
      position.fallbacks ?? [],
      useAnimationFrame,
    );
  }
}

export type AnchorPositioningRoot = Document | HTMLElement | ShadowRoot;

export interface AnchorPositioningPolyfillOptions {
  // Whether to use `requestAnimationFrame()` when updating target elements’
  // positions
  useAnimationFrame?: boolean;

  // An array of explicitly targeted elements to polyfill
  elements?: HTMLElement[];

  /**
   * Set the root elements that are queried when looking for anchors and targets.
   * @default document
   */
  roots?: AnchorPositioningRoot[];

  // Whether to exclude elements with eligible inline styles. When not defined
  // or set to `false`, the polyfill will be applied to all elements that have
  // eligible inline styles, regardless of whether the `elements` option is
  // defined. When set to `true`, elements with eligible inline styles listed
  // in the `elements` option will still be polyfilled, but no other elements
  // in the document will be implicitly polyfilled.
  excludeInlineStyles?: boolean;

  /**
   * Controls whether the polyfill wraps a `position-area` target with an
   * element that approximates the containing block created by `position-area`.
   *
   * - `true` (default): always add the wrapper. This matches the specified
   *   behavior most closely, but the extra element can interfere with author
   *   CSS (e.g. direct child/sibling selectors).
   * - `false`: never add the wrapper. The polyfill instead computes and applies
   *   inset values on the target itself. This avoids the extra element, but
   *   styles that resolve against the containing block (percentage sizes, `auto`
   *   or percentage margins, percentage padding, or stretch/`anchor-center`
   *   self-alignment) will not match the native behavior.
   * - `'auto'`: add the wrapper only for targets whose styles resolve against
   *   the containing block; other targets are positioned directly.
   * @default true
   */
  positionAreaContainingBlock?: PositionAreaContainingBlock;
}

/** @internal */
export interface NormalizedAnchorPositioningPolyfillOptions {
  elements?: HTMLElement[];
  excludeInlineStyles?: boolean;
  positionAreaContainingBlock: PositionAreaContainingBlock;
  roots: AnchorPositioningRoot[];
  useAnimationFrame?: boolean;
}

function normalizePolyfillOptions(
  useAnimationFrameOrOption: boolean | AnchorPositioningPolyfillOptions = {},
): NormalizedAnchorPositioningPolyfillOptions {
  const options =
    typeof useAnimationFrameOrOption === 'boolean'
      ? { useAnimationFrame: useAnimationFrameOrOption }
      : useAnimationFrameOrOption;
  const useAnimationFrame =
    options.useAnimationFrame === undefined
      ? Boolean(window.UPDATE_ANCHOR_ON_ANIMATION_FRAME)
      : options.useAnimationFrame;

  if (!Array.isArray(options.elements)) {
    options.elements = undefined;
  }

  if (!Array.isArray(options.roots) || options.roots.length === 0) {
    options.roots = [document];
  }

  return Object.assign(options, {
    useAnimationFrame,
    positionAreaContainingBlock: options.positionAreaContainingBlock ?? true,
  }) as NormalizedAnchorPositioningPolyfillOptions;
}

// Support a boolean option for backwards compatibility.
export async function polyfill(
  useAnimationFrameOrOption?: boolean | AnchorPositioningPolyfillOptions,
): Promise<AnchorPositions> {
  const options = normalizePolyfillOptions(
    useAnimationFrameOrOption ?? window.ANCHOR_POSITIONING_POLYFILL_OPTIONS,
  );

  // fetch CSS from stylesheet and inline style
  let styleData = await fetchCSS(options);

  // eslint-disable-next-line no-useless-assignment
  let rules: AnchorPositions = {};
  let inlineStyles: Map<HTMLElement, Record<string, string>> | undefined;
  let positionAreaStyles: GeneratedStyles;

  // Reset the CSS parse errors in case the polyfill is run multiple times, and
  // at the beginning in case a previous run failed.
  resetParseErrors();

  // If the polyfill fails during the steps in the try catch, it is likely due
  // to invalid CSS, so report the CSS parse errors. Subsequent errors are less
  // likely to be caused by parse errors.
  try {
    // pre parse CSS styles that we need to cascade
    const cascadeCausedChanges = cascadeCSS(styleData, options.roots);
    if (cascadeCausedChanges) {
      styleData = transformCSS(styleData, undefined, options.roots);
    }
    // parse CSS
    const parsedCSS = await parseCSS(styleData, options);
    rules = parsedCSS.rules;
    inlineStyles = parsedCSS.inlineStyles;
    positionAreaStyles = parsedCSS.positionAreaStyles;
  } catch (error) {
    reportParseErrorsOnFailure();
    throw error;
  }

  if (Object.values(rules).length) {
    // update source code
    transformCSS(styleData, inlineStyles, options.roots);
    insertGeneratedStyles(positionAreaStyles);

    // calculate position values
    await position(rules, options.useAnimationFrame);
  }

  return rules;
}
